import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { useTheme } from 'styled-components';
import useSWR from 'swr';
import { IssueType } from '../../../../../../../typings/Issue.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import LINKS from '../../../../configs/links';
import { fieldName } from '../../../../utils';
import { issue_actions, ISSUE_STATUS_CONFIGS, issue_type_configs } from '../../../../utils/issues';
import Button, { ButtonGroup, ClickableArea } from '../../../common/base/Button';
import Icon from '../../../common/base/Icon';
import Tabs from '../../../common/base/Tabs';
import Dropdown from '../../../common/Dropdown';
import DropdownMenu from '../../../common/DropdownMenu';
import EmptyState from '../../../common/EmptyState';
import CheckboxInput from '../../../common/Form/Fields/CheckboxInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary, ViewTitle } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import IssuesList, { useIssuesList } from './IssueList';
const sort_options = {
  first_seen_at: 'First seen',
  last_seen_at: 'Last seen'
} as const;
const sort_keys = Object.keys(sort_options) as (keyof typeof sort_options)[];
const IssuesView: React.FC = () => {
  const $ = _c(128);
  const theme = useTheme();
  const {
    addToast
  } = useToasts();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    has_connection
  } = useContext(DashboardContext);
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const types = query.type && query.type.length > 0 ? query.type : undefined;
  const [sort_by, setSortBy] = useLocalStorage("pref:issues_sort_by", sort_keys[0]);
  const current_status = query.tab && ISSUE_STATUS_CONFIGS[query.tab] ? query.tab : "OPENED";
  const {
    issues,
    issues_pages,
    mutateIssuesPages
  } = useIssuesList(types, current_status, sort_by, undefined);
  let t0;
  if ($[0] !== query.type) {
    t0 = APIMethodKeys.issues.count({
      status: "OPENED",
      type: query.type || undefined
    });
    $[0] = query.type;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== HookdeckAPI.issues || $[3] !== query.type) {
    t1 = () => HookdeckAPI.issues.count({
      status: "OPENED",
      type: query.type || undefined
    });
    $[2] = HookdeckAPI.issues;
    $[3] = query.type;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data: open_count,
    mutate: mutateOpenedCount
  } = useSWR(t0, t1);
  let t2;
  if ($[5] !== query.type) {
    t2 = APIMethodKeys.issues.count({
      status: "ACKNOWLEDGED",
      type: query.type || undefined
    });
    $[5] = query.type;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== HookdeckAPI.issues || $[8] !== query.type) {
    t3 = () => HookdeckAPI.issues.count({
      status: "ACKNOWLEDGED",
      type: query.type || undefined
    });
    $[7] = HookdeckAPI.issues;
    $[8] = query.type;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data: ack_count,
    mutate: mutateAcknowledgedCount
  } = useSWR(t2, t3);
  let t4;
  if ($[10] !== query.type) {
    t4 = APIMethodKeys.issues.count({
      status: "RESOLVED",
      type: query.type || undefined
    });
    $[10] = query.type;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== HookdeckAPI.issues || $[13] !== query.type) {
    t5 = () => HookdeckAPI.issues.count({
      status: "RESOLVED",
      type: query.type || undefined
    });
    $[12] = HookdeckAPI.issues;
    $[13] = query.type;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    data: resoled_count,
    mutate: mutateResolvedCount
  } = useSWR(t4, t5);
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = APIMethodKeys.issues.count({
      status: "IGNORED"
    });
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== HookdeckAPI.issues || $[17] !== query.type) {
    t7 = () => HookdeckAPI.issues.count({
      status: "IGNORED",
      type: query.type || undefined
    });
    $[16] = HookdeckAPI.issues;
    $[17] = query.type;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    data: ignored_count,
    mutate: mutateIgnoredCount
  } = useSWR(t6, t7);
  let t8;
  if ($[19] !== current_status || $[20] !== mutateAcknowledgedCount || $[21] !== mutateIgnoredCount || $[22] !== mutateOpenedCount || $[23] !== mutateResolvedCount) {
    t8 = async action => {
      if (action === "acknowledge") {
        mutateAcknowledgedCount();
      } else {
        if (action === "resolve") {
          mutateResolvedCount();
        } else {
          if (action === "ignore") {
            mutateIgnoredCount();
          } else {
            if (action === "reopen") {
              mutateOpenedCount();
            }
          }
        }
      }
      if (current_status === "OPENED") {
        mutateOpenedCount();
      } else {
        if (current_status === "ACKNOWLEDGED") {
          mutateAcknowledgedCount();
        } else {
          if (current_status === "RESOLVED") {
            mutateResolvedCount();
          } else {
            if (current_status === "IGNORED") {
              mutateIgnoredCount();
            }
          }
        }
      }
    };
    $[19] = current_status;
    $[20] = mutateAcknowledgedCount;
    $[21] = mutateIgnoredCount;
    $[22] = mutateOpenedCount;
    $[23] = mutateResolvedCount;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const onIssueAction = t8;
  const {
    default_action,
    current_action
  } = ISSUE_STATUS_CONFIGS[current_status || "OPENED"];
  let t9;
  if ($[25] !== HookdeckAPI.issues || $[26] !== addToast || $[27] !== issues_pages || $[28] !== mutateIssuesPages || $[29] !== onIssueAction) {
    t9 = async (issues_id, action_0) => {
      Promise.all(issues_id.map(id => {
        if (action_0 === "acknowledge") {
          return HookdeckAPI.issues.update(id, {
            status: "ACKNOWLEDGED"
          });
        } else {
          if (action_0 === "resolve") {
            return HookdeckAPI.issues.update(id, {
              status: "RESOLVED"
            });
          } else {
            if (action_0 === "reopen") {
              return HookdeckAPI.issues.update(id, {
                status: "OPENED"
              });
            } else {
              if (action_0 === "ignore") {
                return HookdeckAPI.issues.update(id, {
                  status: "IGNORED"
                });
              } else {
                if (action_0 === "dismiss") {
                  return HookdeckAPI.issues.dismiss(id);
                }
              }
            }
          }
        }
      })).then(updated_issues => {
        onIssueAction(action_0);
        addToast("success", "Issues status updated");
        const updated_ids = new Set(updated_issues.map(_temp).filter(Boolean));
        mutateIssuesPages(issues_pages?.map(page => ({
          ...page,
          models: action_0 === "dismiss" ? page.models.map(issue_0 => updated_issues.find(updated_issue => updated_issue && updated_issue.id === issue_0.id) || issue_0) : page.models.filter(issue_1 => !updated_ids.has(issue_1.id))
        })));
      }).catch(() => addToast("error", "Failed to update issues"));
    };
    $[25] = HookdeckAPI.issues;
    $[26] = addToast;
    $[27] = issues_pages;
    $[28] = mutateIssuesPages;
    $[29] = onIssueAction;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const applyAction = t9;
  const t10 = open_count?.count;
  const t11 = ack_count?.count;
  const t12 = resoled_count?.count;
  const t13 = ignored_count?.count;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[31] !== applyAction || $[32] !== current_action || $[33] !== current_status || $[34] !== default_action || $[35] !== issues || $[36] !== query.type || $[37] !== setSortBy || $[38] !== sort_by || $[39] !== t10 || $[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== updateSearchQuery) {
    const counts = {
      OPENED: t10,
      ACKNOWLEDGED: t11,
      RESOLVED: t12,
      IGNORED: t13
    };
    T4 = StyledViewLayout;
    T3 = StyledViewContent;
    T2 = StyledViewNav;
    let t19;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <ViewTitle title="Issues" tooltip={"Issues track problems in your project. There are four issue types \u2014 Delivery, Transformation, Backpressure, and Request \u2014 each of which are associated with Issue Triggers that allow you to determine your alerting preferences."} link={LINKS.product_docs.issues} />;
      $[54] = t19;
    } else {
      t19 = $[54];
    }
    let t20;
    if ($[55] !== setSortBy || $[56] !== sort_by) {
      t20 = sort_keys.map(key => ({
        onClick: () => setSortBy(key),
        label: sort_options[key],
        selected: sort_by === key
      }));
      $[55] = setSortBy;
      $[56] = sort_by;
      $[57] = t20;
    } else {
      t20 = $[57];
    }
    const t21 = sort_options[sort_by];
    let t22;
    if ($[58] !== t20 || $[59] !== t21) {
      t22 = <DropdownMenu options={t20} p={0} parent_width={true} placement="bottom-end" label={t21} expand_icon="chevron_down" icon="sort" outline={true} />;
      $[58] = t20;
      $[59] = t21;
      $[60] = t22;
    } else {
      t22 = $[60];
    }
    let t23;
    if ($[61] !== query.type) {
      t23 = query.type ? query.type.reduce(_temp2, {}) : {};
      $[61] = query.type;
      $[62] = t23;
    } else {
      t23 = $[62];
    }
    let t24;
    if ($[63] !== t23) {
      t24 = {
        type: t23
      };
      $[63] = t23;
      $[64] = t24;
    } else {
      t24 = $[64];
    }
    let t25;
    if ($[65] !== updateSearchQuery) {
      t25 = values => {
        const type = Object.entries(values.type).filter(_temp3).map(_temp4);
        updateSearchQuery({
          type: type.length > 0 ? type : undefined
        });
      };
      $[65] = updateSearchQuery;
      $[66] = t25;
    } else {
      t25 = $[66];
    }
    let t26;
    if ($[67] !== query.type) {
      t26 = t27 => {
        const {
          submitForm
        } = t27;
        return <Dropdown parent_width={{
          min: 272
        }} placement="bottom-end" icon="filter_list" label="Issue type" onToggle={() => {
          submitForm();
        }} expand_icon="chevron_down" badge={query.type && query.type.length > 0 ? {
          label: String(query.type.length)
        } : undefined} outline={true}>{_temp6}</Dropdown>;
      };
      $[67] = query.type;
      $[68] = t26;
    } else {
      t26 = $[68];
    }
    let t27;
    if ($[69] !== t24 || $[70] !== t25 || $[71] !== t26) {
      t27 = <Formik initialValues={t24} onSubmit={t25}>{t26}</Formik>;
      $[69] = t24;
      $[70] = t25;
      $[71] = t26;
      $[72] = t27;
    } else {
      t27 = $[72];
    }
    let t28;
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = Object.keys(issue_actions);
      $[73] = t28;
    } else {
      t28 = $[73];
    }
    let t29;
    if ($[74] !== current_action || $[75] !== default_action) {
      t29 = t28.filter(key_3 => !(key_3 === current_action || key_3 === default_action));
      $[74] = current_action;
      $[75] = default_action;
      $[76] = t29;
    } else {
      t29 = $[76];
    }
    let t30;
    if ($[77] !== applyAction || $[78] !== issues || $[79] !== t29) {
      t30 = t29.map(key_4 => ({
        label: `${issue_actions[key_4].label} all`,
        role: issue_actions[key_4].role,
        icon: issue_actions[key_4].icon,
        onClick: () => applyAction(issues.map(_temp7), key_4),
        danger: issue_actions[key_4].danger
      }));
      $[77] = applyAction;
      $[78] = issues;
      $[79] = t29;
      $[80] = t30;
    } else {
      t30 = $[80];
    }
    const t31 = issue_actions[default_action];
    let t32;
    if ($[81] !== applyAction || $[82] !== default_action || $[83] !== issues) {
      t32 = () => applyAction(issues.map(_temp8), default_action);
      $[81] = applyAction;
      $[82] = default_action;
      $[83] = issues;
      $[84] = t32;
    } else {
      t32 = $[84];
    }
    const t33 = issue_actions[default_action];
    let t34;
    if ($[85] !== t33.icon) {
      t34 = <Icon icon={t33.icon} muted={true} left={true} />;
      $[85] = t33.icon;
      $[86] = t34;
    } else {
      t34 = $[86];
    }
    const t35 = issue_actions[default_action];
    let t36;
    if ($[87] !== t31.role || $[88] !== t32 || $[89] !== t34 || $[90] !== t35.label) {
      t36 = <Button.Permission role={t31.role} outline={true} onClick={t32}>{t34}{t35.label} all</Button.Permission>;
      $[87] = t31.role;
      $[88] = t32;
      $[89] = t34;
      $[90] = t35.label;
      $[91] = t36;
    } else {
      t36 = $[91];
    }
    let t37;
    if ($[92] !== t30 || $[93] !== t36) {
      t37 = <ButtonGroup more_options={t30}>{t36}</ButtonGroup>;
      $[92] = t30;
      $[93] = t36;
      $[94] = t37;
    } else {
      t37 = $[94];
    }
    if ($[95] !== t22 || $[96] !== t27 || $[97] !== t37) {
      t18 = <StyledViewNavHeader>{t19}<StyledViewNavActions>{t22}{t27}{t37}</StyledViewNavActions></StyledViewNavHeader>;
      $[95] = t22;
      $[96] = t27;
      $[97] = t37;
      $[98] = t18;
    } else {
      t18 = $[98];
    }
    T1 = StyledViewNavSecondary;
    T0 = Tabs;
    t14 = current_status;
    if ($[99] !== updateSearchQuery) {
      t15 = tab => updateSearchQuery({
        tab
      });
      $[99] = updateSearchQuery;
      $[100] = t15;
    } else {
      t15 = $[100];
    }
    t16 = false;
    t17 = Object.entries(ISSUE_STATUS_CONFIGS).map(t38 => {
      const [key_5, status_config] = t38;
      return {
        key: key_5,
        label: status_config.label,
        icon: status_config.icon,
        badge_theme: status_config.badge_theme,
        badge_label: counts[key_5] || 0
      };
    });
    $[31] = applyAction;
    $[32] = current_action;
    $[33] = current_status;
    $[34] = default_action;
    $[35] = issues;
    $[36] = query.type;
    $[37] = setSortBy;
    $[38] = sort_by;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = updateSearchQuery;
    $[44] = T0;
    $[45] = T1;
    $[46] = T2;
    $[47] = T3;
    $[48] = T4;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
  } else {
    T0 = $[44];
    T1 = $[45];
    T2 = $[46];
    T3 = $[47];
    T4 = $[48];
    t14 = $[49];
    t15 = $[50];
    t16 = $[51];
    t17 = $[52];
    t18 = $[53];
  }
  let t19;
  if ($[101] !== T0 || $[102] !== t14 || $[103] !== t15 || $[104] !== t16 || $[105] !== t17) {
    t19 = <T0 active_tab={t14} onTabSelected={t15} border={t16} tabs={t17} />;
    $[101] = T0;
    $[102] = t14;
    $[103] = t15;
    $[104] = t16;
    $[105] = t17;
    $[106] = t19;
  } else {
    t19 = $[106];
  }
  let t20;
  if ($[107] !== T1 || $[108] !== t19) {
    t20 = <T1>{t19}</T1>;
    $[107] = T1;
    $[108] = t19;
    $[109] = t20;
  } else {
    t20 = $[109];
  }
  let t21;
  if ($[110] !== T2 || $[111] !== t18 || $[112] !== t20) {
    t21 = <T2>{t18}{t20}</T2>;
    $[110] = T2;
    $[111] = t18;
    $[112] = t20;
    $[113] = t21;
  } else {
    t21 = $[113];
  }
  let t22;
  if ($[114] !== applyAction || $[115] !== current_status || $[116] !== has_connection || $[117] !== sort_by || $[118] !== theme || $[119] !== types) {
    t22 = has_connection ? <IssuesList types={types} status={current_status} sort_by={sort_by} applyAction={applyAction} /> : <EmptyState title="Track and resolve connection problems" description={"Issues track problems on your connections, alerts to which are determined by Issue Triggers. You don\u2019t have any issues because you haven\u2019t yet made a connection."} asset={`issues-${theme.mode}.png`} asset_position="background" cta={{
      label: "Create first connection",
      icon: "connections",
      to: "/create-first-connection"
    }} />;
    $[114] = applyAction;
    $[115] = current_status;
    $[116] = has_connection;
    $[117] = sort_by;
    $[118] = theme;
    $[119] = types;
    $[120] = t22;
  } else {
    t22 = $[120];
  }
  let t23;
  if ($[121] !== T3 || $[122] !== t21 || $[123] !== t22) {
    t23 = <T3>{t21}{t22}</T3>;
    $[121] = T3;
    $[122] = t21;
    $[123] = t22;
    $[124] = t23;
  } else {
    t23 = $[124];
  }
  let t24;
  if ($[125] !== T4 || $[126] !== t23) {
    t24 = <T4>{t23}</T4>;
    $[125] = T4;
    $[126] = t23;
    $[127] = t24;
  } else {
    t24 = $[127];
  }
  return t24;
};
export default IssuesView;
function _temp(issue) {
  return issue && issue.id;
}
function _temp2(obj, key_0) {
  return {
    ...obj,
    [key_0]: true
  };
}
function _temp3(t0) {
  const [, value] = t0;
  return value;
}
function _temp4(t0) {
  const [key_1] = t0;
  return key_1;
}
function _temp5(t0) {
  const [key_2, config] = t0;
  const fieldId = fieldName(key_2, "type");
  return <ClickableArea key={key_2} rounded={true}><CheckboxInput label={config.label} icon={config.icon} name={fieldId} p={{
      x: 2,
      y: 1.5
    }} /></ClickableArea>;
}
function _temp6() {
  return <Form><Div p={1}>{Object.entries(issue_type_configs).map(_temp5)}</Div></Form>;
}
function _temp7(issue_2) {
  return issue_2.id;
}
function _temp8(issue_3) {
  return issue_3.id;
}